import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewContainerRef
} from '@angular/core';
import { delay, forkJoin, map, mergeMap, Observable, of, Subject, Subscription, switchMap, tap } from 'rxjs';
import {
  AnyObject,
  AuthCache,
  CardStructureConfig,
  CommonDataSetAction,
  ElementDataSet,
  FormFieldConfig,
  GeometryPoint,
  ItemAttrListDataSet,
  ItemBodyRender,
  ItemIconRender,
  ItemMiddleAttrListDataSet,
  ListableComponent,
  ListDataSetConfig,
  ListDataSortConfig,
  PaginateDto,
  ResponseList,
  TableDataColumn,
  ValueType
} from '@mri/types';
import { MatDrawer } from '@angular/material/sidenav';
import { AuthResourceService, ConfigResourceService, GenericResourceService } from '@mri/app/resources';
import { environment } from '@mri/environment';
import { Socket, SocketIoConfig } from 'ngx-socket-io';
import { catchError } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GlobalMapService } from '../global-map';
import { MatDialog } from '@angular/material/dialog';
import {
  alwaysObservable,
  chunkArray,
  excelGenerator,
  ExcelReportDTO,
  getMapPadding,
  pdfGenerator,
  PdfReportDTO
} from '@mri/functions';
import { storageManager } from '@mri/constants';
import { LoadingService } from '../loading';
import { tableGetCellRender, tableGetMiddleClass, tableGetMiddleRender } from '../table-data-set';
import {
  cardGetActionIconRender,
  cardGetBodyRender,
  cardGetIconRender,
  cardGetImgRender,
  cardGetTitleRender
} from '../card-data-set';

import MarkerLabel = google.maps.MarkerLabel;
import Polyline = google.maps.Polyline;
import Polygon = google.maps.Polygon;
import Marker = google.maps.Marker;
import { ColumnManagementComponent } from './components/column-management/column-management.component';

type DynamicDrawer = {
  componentName: string;
  title?: string;
  component?: AnyObject;
  data?: AnyObject;
  index: number;
}

interface RenderedCardElement {
  icon?: ItemIconRender;
  img?: string;
  title: string;
  body: Array<ItemBodyRender>;
  actionIcons: Record<string, string>;
}

interface RenderedElement {
  _id: string;
  type?: string;
  label?: string;
  table?: {
    view: Record<string, AnyObject>;
    viewClass?: Record<string, string>;
    middleView?: Record<string, AnyObject>;
    middleViewClass?: Record<string, string>;
  };
  origin: AnyObject;
  card?: RenderedCardElement;
}

interface RenderedElementHash {
  rendered: RenderedElement;
  index: number;
}

@Component({
  selector: 'app-list-map-data-set',
  templateUrl: './list-map-data-set.component.html',
  styleUrl: './list-map-data-set.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ListMapDataSetComponent implements OnInit, OnDestroy, ListableComponent {
  private _filterKey: string;
  private _fixedKey: string;
  private _columnConfigKey: string;
  private _mapElements: Record<string, Array<Marker | MarkerLabel | Polygon | Polyline>> = {};
  private _mapInfoWindow?: google.maps.InfoWindow;
  private _reload$ = new Subject<PaginateDto>();
  private _drawerConfig$ = new Subject<DynamicDrawer>();
  private _reloadFixed$ = new Subject<Array<string>>();
  private _socketElementEvent$ = new Subject<AnyObject>();
  private _openColumnManagement$ = new Subject<AnyObject>();
  private _subscriptions = new Subscription;
  private _initLoad = true;
  private _gatewayUrl?: string;
  private _hasSocket?: boolean;
  private _socket: Socket;
  private _config: ListDataSetConfig;
  private _saveFilterExclude: Array<string> = [];
  private _extraQueryFilter: Record<string, ValueType | Array<ValueType>> | undefined;
  private _sortKey: string;
  private _pagesIds?: { _id: string }[][];
  private _currentPageNumber: number = 1;

  _columns?: TableDataColumn[];
  _filterValueRecord: Record<string, ValueType | ValueType[]> | undefined;
  _currentSort?: ListDataSortConfig;
  _acsSort?: boolean;

  columnKeys: string[] = [];
  showedColumns: TableDataColumn[] = [];
  dynamicDrawers: Array<DynamicDrawer> = [];
  fixedIds = new Set<string>();
  indicatorIds: Set<string> = new Set();
  tagElementHeight: Record<string, number> = {};
  hideRows = new Set<string>();
  modeFilter?: string;
  selectedElementIds: Set<string> = new Set;
  filterFields?: FormFieldConfig[];
  searchFilterFields?: FormFieldConfig[];
  filterFieldsDrawer?: FormFieldConfig[];
  actions!: CommonDataSetAction[];
  countFindElements!: number;
  isLoading: boolean;
  structure?: CardStructureConfig<AnyObject>;
  renderedElementList: Array<RenderedElement> = [];
  renderedElementHashes: Record<string, RenderedElementHash> = {};
  loadingHolderList = [...new Array(50)];


  @ViewChild('drawerFilter') drawerFilter: MatDrawer;
  @ViewChildren('drawerContainer', { read: ViewContainerRef }) drawerContainers!: QueryList<ViewContainerRef>;
  @ViewChildren('drawerDynamic', { read: MatDrawer }) drawerDynamics!: QueryList<MatDrawer>;


  @Input() cardContainerWidth = 300;
  @Input() columnShowKeys?: string[];
  @Input() joints: Record<string, Record<string, AnyObject>> = {};
  @Input() marks: Record<string, Record<string, boolean>> = {};
  @Input() mode: string = 'table';
  @Input() sources: AnyObject[];
  @Input() fixedSources: AnyObject[];
  @Input() size: number = 50;
  @Input() autoSize?: boolean;
  @Input() autoShowFilter?: boolean;

  @Output() filterValueRecordChange = new EventEmitter<Record<string, ValueType | ValueType[]>>();
  @Output() actionListener = new EventEmitter<{ action: string, elements: Array<AnyObject> }>();
  @Output() receiverSocketElement = new EventEmitter<AnyObject>();
  @Output() doubleElementsListener = new EventEmitter<AnyObject>();

  @Input({ required: true })
  set config(config: ListDataSetConfig) {
    this._config = config;

    const {
      hasSocket,
      structure,
      columns,
      actions,
      gatewayUrl,
      filter,
      noInitLoad,
      modeFilter,
      modeViewDefault,
      saveFilterExclude,
      extraQueryFilter, searchFilter
    } = config;

    this._gatewayUrl = gatewayUrl;
    this._hasSocket = hasSocket;
    this.structure = structure;
    this.filterFields = filter;
    this.filterFieldsDrawer = filter?.map(f => ({ ...f, containerClass: '' })) || [];
    this._columns = columns;
    this.actions = actions;
    this.modeFilter = modeFilter;
    this._initLoad = noInitLoad === undefined ? true : !noInitLoad;
    this._saveFilterExclude = saveFilterExclude || [];
    this._extraQueryFilter = extraQueryFilter;
    this.searchFilterFields = searchFilter;
    this.mode = modeViewDefault || 'table';

    this._organizeColumns();
  }

  @Input()
  set filterValueRecord(value: Record<string, ValueType | ValueType[]> | undefined) {
    if (!value) return;
    this._filterValueRecord = value;
  }

  get elements(): Array<AnyObject & ElementDataSet> {
    return [...(this.fixedSources || []), ...(this.sources || [])];
  }

  get dialogSrv(): MatDialog {
    return this._dialogSrv;
  }

  get filterValues(): Record<string, ValueType | ValueType[]> {
    return this._filterValueRecord || {} as Record<string, ValueType | ValueType[]>;
  }

  get selectedElementList(): AnyObject[] {
    return this.sources?.filter(e => this.selectedElementIds.has(e._id)) || [];
  }

  get actionsInRow() {
    return this.actions.filter(a => a.inRow);
  }

  get filterCount() {
    if (!this._filterValueRecord) return 0;
    return Object.values(this._filterValueRecord).reduce((acc, value) => {
      if (!value) return acc;
      if (Array.isArray(value)) return acc + value.length;
      return acc + 1;
    }, 0);
  }

  get map(): google.maps.Map {
    return this._mapSrv.map;
  }

  get authSrv(): AuthResourceService {
    return this._authSrv;
  }

  get genericResourceSrv(): GenericResourceService {
    return this._genericSrcSrv;
  }

  get loadingSrv(): LoadingService {
    return this._loadingSrv;
  }

  get configSrv(): ConfigResourceService {
    return this._configSrv;
  }

  constructor(
    private _genericSrcSrv: GenericResourceService,
    private _changeDetector: ChangeDetectorRef,
    private _configSrv: ConfigResourceService,
    private _authSrv: AuthResourceService,
    private _loadingSrv: LoadingService,
    private _mapSrv: GlobalMapService,
    private _snackBar: MatSnackBar,
    private _dialogSrv: MatDialog
  ) {


    this.configKeys();
  }

  configKeys(){
    const pathname = location.pathname;
    this._filterKey = `filter:${pathname}`;
    this._sortKey = `sort:${pathname}`;
    this._fixedKey = `fixed:${pathname}`;
    this._columnConfigKey = `column-config:${pathname}`;
  }

  ngOnInit() {
    this.loadingHolderList = [...new Array(this.size)];
    this.configKeys();
    const valueFilterSaved = storageManager.getJson(this._filterKey);
    const valueSort = storageManager.getJson<AnyObject>(this._sortKey);
    this._currentSort = valueSort;
    this._acsSort = valueSort?.acsSort;

    if (!this._filterValueRecord)
      this._filterValueRecord = Object.keys(valueFilterSaved || {})
        .reduce((previousValue, key) => {
          if (this._saveFilterExclude.includes(key)) return previousValue;
          previousValue[key] = valueFilterSaved[key];
          return previousValue;
        }, {} as Record<string, AnyObject>);

    this.fixedIds = new Set(storageManager.getJson(this._fixedKey));

    this._subscriptions.add(
      this._reload$.pipe(
        tap(page => this._currentPageNumber = page.page),
        switchMap(paginate => this._reloadData(paginate)),
        tap(() => this._changeDetector.markForCheck())
      ).subscribe()
    );

    this._subscriptions.add(
      this._authSrv.auth$
        .pipe(
          tap(auth => auth && this._connectSocket(auth)),
          tap(() => this._initLoad && this.changePage()),
          tap(() => this._reloadFixed$.next([...(this.fixedIds || [])])),
          tap(() => this.autoShowFilter && this.drawerFilter.open())
        ).subscribe()
    );

    this._subscriptions.add(
      this._mapSrv.map$
        .pipe(
          tap(() => {
            this._loadingSrv.visible = true;
            this._changeDetector.markForCheck();
          }),
          tap(() => this._reAssignMap()),
          mergeMap(() => alwaysObservable(this._config.onLoadElements?.(this.getExtraData(null)))),
          mergeMap(() => alwaysObservable(this._config.onMapReady?.(this.getExtraData(null)))),
          tap(() => {
            this._loadingSrv.visible = false;
            this._changeDetector.markForCheck();
          })
        ).subscribe()
    );

    this._subscriptions.add(
      this._socketElementEvent$
        .pipe(
          mergeMap(element => forkJoin([of(element), alwaysObservable(this._config.onLoadElements?.(this.getExtraData(element)))])),
          mergeMap(([element]) => alwaysObservable(this._config.onMapReady?.(this.getExtraData(element)))),
          tap(() => this._changeDetector.markForCheck())
        ).subscribe()
    );

    this._subscriptions.add(
      this._drawerConfig$
        .pipe(
          delay(200),
          tap(element => this._initDrawer(element)),
          mergeMap(element => {
            const { index } = element;

            const hidden$ = this.dynamicDrawers.map((value, i) => {
              if (i <= index) return of(null);

              return of(this.drawerDynamics.get(i)?.close());
            });

            return forkJoin(hidden$)
              .pipe(map(() => element));
          }),
          tap(element => {
            const { index } = element;

            while (index + 1 > this.dynamicDrawers.length) {
              this.dynamicDrawers.pop();
            }
          }),
          tap(() => this._changeDetector.markForCheck())
        ).subscribe()
    );

    this._subscriptions.add(
      this.doubleElementsListener
        .pipe(
          mergeMap(element => alwaysObservable(this._config.onDlbClickElements?.(this.getExtraData(element)))),
          tap(() => this._changeDetector.markForCheck())
        ).subscribe()
    );

    this._subscriptions.add(
      this._openColumnManagement$
        .pipe(
          mergeMap(() => this._openColumnManagement()),
          tap(() => this._changeDetector.markForCheck())
        ).subscribe()
    );



    if (this.mode === 'map') {
      this.showMap();
    }
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
    this._mapSrv.visible = false;

    Object.keys(this._mapElements).forEach(id => this.removeMapElements(id));
  }

  getExtraData(element?: AnyObject): ItemAttrListDataSet {
    const elements = this.renderedElementList.map(e => e.origin);

    return {
      element,
      elements,
      component: this,
      marks: this.marks,
      joint: this.joints,
      totalCount: this.countFindElements,
      filterValueRecord: this._filterValueRecord || {},
      selectedElements: this.selectedElementList
    };
  }

  getExtraDataMiddle(element: AnyObject, index: number): ItemMiddleAttrListDataSet {
    return {
      ...this.getExtraData(element),
      prevElement: (this.sources || [])[index - 1]
    };
  }

  onChangeFilterValues(values: Record<string, ValueType | ValueType[]>, paginator: PaginateDto = { page: 1, size: this.size }) {
    const saveValues: Record<string, ValueType | ValueType[]> = {};

    Object.keys(values)
      .filter(key => !this._saveFilterExclude.includes(key))
      .forEach(key => saveValues[key] = values[key]);

    this._filterValueRecord = values;
    this.filterValueRecordChange.emit(values);

    this.drawerFilter.close().then();
    this._reload$.next(paginator);
  }

  reload(paginator?: PaginateDto) {
    this.onChangeFilterValues(this._filterValueRecord || {}, paginator);
  }

  reloadSync(paginator: PaginateDto = { page: 1, size: this.size }): Observable<AnyObject> {
    return this._reloadData(paginator);
  }

  changePage(paginate: PaginateDto = { page: 1, size: this.size }) {
    this._reload$.next(paginate);
  }

  pressAction(action: CommonDataSetAction, elements?: Array<AnyObject>) {
    elements = elements || this.selectedElementList;

    this.actionListener.next({ action: action.key, elements });

    if (action.click) {
      const result = action.click({ ...this.getExtraData(null), selectedElements: elements });

      if (result instanceof Observable) {
        this._subscriptions.add(result.subscribe());
      }
    }

  }

  toggleSelect(element: AnyObject) {
    if (this.selectedElementIds.has(element._id)) {
      this.selectedElementIds.delete(element._id);
    } else {
      this.selectedElementIds.add(element._id);
    }
    this.selectedElementIds = new Set(this.selectedElementIds); // Trigger change detection

    this._subscriptions.add(
      alwaysObservable(this._config.onSelectedElements?.(this.getExtraData(null)))
        .subscribe()
    );

    this.updateRender();

    this.scrollToElementId(element._id);
  }

  onSaveFilterValues(values: AnyObject) {
    const saveValues: Record<string, ValueType | ValueType[]> = {};

    Object.keys(values)
      .filter(key => !this._saveFilterExclude.includes(key))
      .forEach(key => saveValues[key] = values[key]);

    storageManager.set(this._filterKey, saveValues);
  }

  onlySelect(origin: AnyObject, element?: AnyObject) {

    this.selectedElementIds = new Set([origin._id]);
    this._subscriptions.add(
      alwaysObservable(this._config.onSelectedElements?.(this.getExtraData(null)))
        .subscribe()
    );

    this.updateRender();

    this.scrollToElementId(origin._id);
  }

  executeSort(config: ListDataSortConfig) {
    if (this._currentSort?.id === config.id) {
      this._acsSort = !this._acsSort;
    } else {
      this._acsSort = true;
    }

    this._currentSort = config;
    storageManager.set(this._sortKey, { ...config, acsSort: this._acsSort });

    this._reload$.next({ page: 1, size: this.size });
  }

  onVisible(element: AnyObject, isVisible: boolean, index: number) {
    if (isVisible) {
      this.onShow(element, index);
    } else {
      this.onHide(element);
    }
  }

  onShow(element: AnyObject, index: number) {

    delete this.tagElementHeight[element._id];
    this.hideRows.delete(element._id);

    if (this.isLoading) return;
    const currentCount = this.sources?.length || 0;
    const size = this.size;

    if (!(currentCount - (size / 4) < index)) return;

    if (this.countFindElements <= currentCount) return;

    const page = this._currentPageNumber + 1;

    this.changePage({ size, page });
  }

  onHide(element: AnyObject) {
    this.hideRows.add(element._id);
  }

  toggleMap() {
    this.mode = this.mode === 'map' ? 'table' : 'map';
    this._mapSrv.visible = this.mode === 'map';
    this.updateRender();
  }

  updateRender() {
    this._changeDetector.markForCheck();
  }

  onDoubleClick(element: AnyObject): void {
    this.doubleElementsListener.emit(element);
  }

  hideMap(): void {
    this.mode = 'table';
    this._mapSrv.visible = false;
  }

  showMap(): void {
    this.mode = 'map';
    this._mapSrv.visible = true;
  }

  addMapElements(id: string, mapElements: Array<Marker | MarkerLabel | Polygon | Polyline>): void {
    this.removeMapElements(id);

    this._mapElements[id] = mapElements;

    if (this._mapSrv.map) {
      mapElements.forEach((e: AnyObject) => e.setMap(this._mapSrv.map));
    }
  }

  removeMapElements(id: string): void {
    const beforeElement = this._mapElements[id];
    if (beforeElement?.length) {
      beforeElement.forEach((e: AnyObject) => e.setMap(null));
    }

    delete this._mapElements[id];
  }

  getMapElements(id: string): Array<Marker | MarkerLabel | Polygon | Polyline> {
    return this._mapElements[id] || [];
  }

  centerMap(geometries: GeometryPoint[]): void {
    if (!this._mapSrv.map || !geometries || geometries.length === 0) return;

    if (geometries.length === 1) {
      geometries[0] && this._centerWithPadding(geometries[0]);

      return;
    }

    const bounds = new google.maps.LatLngBounds();

    geometries.forEach(({ coordinates }) => {
      if (coordinates) {
        bounds.extend(new google.maps.LatLng(coordinates[1] as number, coordinates[0] as number));
      }
    });

    this._mapSrv.map.fitBounds(bounds, getMapPadding());
  }

  scrollToElementId(id: string): boolean {
    const element = document.getElementById(`item-element-${id}`);
    if (!element) return false;

    element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });

    return true;
  }

  openInfoWindow(option: google.maps.InfoWindowOptions, marker?: Marker) {
    if (!this._mapInfoWindow) {
      this._mapInfoWindow = new google.maps.InfoWindow();
    }

    this._mapInfoWindow.setOptions(option);
    this._mapInfoWindow.open(this._mapSrv.map, marker);
  }

  indicateIds(ids: string[]) {
    this.indicatorIds = new Set(ids);
    setTimeout(() => {
      this.indicatorIds.clear();
      this._changeDetector.markForCheck();
    }, 8000);
  }

  openDrawer(title: string, component: AnyObject, data: AnyObject) {
    const componentName = component.name;
    let index = this.dynamicDrawers?.findIndex(d => d.componentName === componentName);

    if (index === -1) {
      index = this.dynamicDrawers.length;
    }
    const newDrawer: DynamicDrawer = { componentName, title, data, component, index };
    this.dynamicDrawers[index] = newDrawer;

    this._drawerConfig$.next(newDrawer);
  }

  markElement(id: string, key: string, value: boolean) {
    if (!this.marks[id]) {
      this.marks[id] = {};
    }
    this.marks[id][key] = value;

    this._changeDetector.markForCheck();
  }

  copyCell(value: AnyObject) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = value as string;
    const valueCleaned = tempDiv.textContent || tempDiv.innerText || '';

    navigator.clipboard.writeText(valueCleaned.toString())
      .catch(err => {
        console.error('Error al copiar el texto: ', err);
      });
  }

  exportExcel(): Observable<AnyObject> {
    return this._reloadAllIfNoComplete()
      .pipe(
        map(() => {
          const dto: ExcelReportDTO = {
            columns: this.showedColumns?.map((column) => column.label || column.key) || [],
            rows: this.renderedElementList.map((row) =>
              this.showedColumns?.map(
                column => {
                  const tempDiv = document.createElement('div');
                  tempDiv.innerHTML = row.table?.view[column.key];
                  return tempDiv.textContent || tempDiv.innerText || '';
                }
              ) || []
            ),
            fileName: `Informe ${document.title}.xls`
          };
          return dto;
        }),
        tap((dto) => excelGenerator(dto))
      );
  }

  exportPdf(): Observable<AnyObject> {
    const tableElement = document.getElementById('table-results');
    if(!tableElement) return of(null);

    return this._reloadAllIfNoComplete()
      .pipe(
        map(() => {
          const dto: PdfReportDTO = {
            tableElement,
            orientation: 'p',
            fileName: `Informe ${document.title}.pdf`
          };
          return dto;
        }),
        tap(dto => pdfGenerator(dto))
      );
  }

  openColumnManagement(): void {
    this._openColumnManagement$.next(1);
  }

  private _openColumnManagement(): Observable<AnyObject> {
    const dialogRef = this._dialogSrv.open(ColumnManagementComponent, {
      width: '400px',
      data: {
        allColumns: this._columns,
        showedColumnKeys: this.showedColumns.map(c => c.key),
      },
    });

    return dialogRef
      .afterClosed()
      .pipe(
        tap((result:Array<TableDataColumn & { checked: boolean }>) => {
          if (!result) return;

          this.showedColumns = result.filter(c => c.checked);
          this.columnKeys = ['$select', ...this.showedColumns.map(c => c.key)];
          storageManager.set(this._columnConfigKey, this.showedColumns.map(c => c.key));
          this._updateRenderElements(this.sources || [], true);
          this._changeDetector.markForCheck();
        })
      );
  }

  private _centerWithPadding(geometry: GeometryPoint) {
    const { coordinates } = geometry;
    const latLng = new google.maps.LatLng(coordinates[1] as number, coordinates[0] as number);


    this._mapSrv.map.panTo(latLng);
  }

  private _reAssignMap(): void {
    if (!this._mapSrv.map) return;
    Object.values(this._mapElements)
      .forEach(elements =>
        elements.forEach((e: AnyObject) => e.setMap(this._mapSrv.map))
      );
  }

  private _getSortFilter(): AnyObject {
    const sortConfig = this._currentSort;

    if (!sortConfig || sortConfig.inView || sortConfig.disable) {
      return {};
    }

    return { [`$sort:${sortConfig.fieldKey}`]: this._acsSort ? 'asc' : 'desc' };
  }

  private _reloadData(paginate: PaginateDto): Observable<AnyObject> {
    if (!this._gatewayUrl) return of(null);

    if (paginate.page === 1) {
      this.sources = [];
      this._clearRenderedElements();
    }

    const { page } = paginate;
    this.isLoading = true;
    this._changeDetector.markForCheck();

    return this._getCountElements()
      .pipe(
        mergeMap(() => this._getListElements(paginate)),
        tap(({ objects }) => {
          this.sources = page === 1 ? objects : [...(this.sources || []), ...objects];
          this._updateRenderElements(objects, page === 1);
        }),
        mergeMap(() => alwaysObservable(this._config.onLoadElements?.(this.getExtraData(null)))),
        tap(() => this._socket?.emit('subscribe', (this.elements || []).map(e => e._id))),
        catchError((e) => {
          console.error(e);
          this._snackBar.open('Error al cargar los elementos', 'Cerrar', {
            duration: 5000,
            panelClass: ['bg-warn-700']
          });
          return of([]);
        }),
        tap(() => {
          this.isLoading = false;
          this.updateRender();
        })
      );
  }

  private _getCountElements(): Observable<ResponseList<{ _id: string }>> {
    if (this._currentPageNumber > 1) {
      return of({
        objects: this._pagesIds?.flat() ?? [],
        count: this.countFindElements
      });
    }

    const url = this._gatewayUrl || '';
    const filter = {
      ...(this._getSortFilter()),
      ...(this._extraQueryFilter || {}),
      ...(this._filterValueRecord || {}),
      nid: [...this.fixedIds]
    };
    return this._genericSrcSrv.countElements(url, { filter })
      .pipe(
        tap(r => {
          this.countFindElements = (r.count) || 0;
          if (Array.isArray(r.objects) && r.objects.length) {
            this._pagesIds = chunkArray(r.objects, this.size);
          }
        })
      );
  }

  private _getListElements(paginate: PaginateDto): Observable<ResponseList<AnyObject>> {
    const url = this._gatewayUrl || '';
    const filter: AnyObject = {
      ...(this._getSortFilter()),
      ...(this._extraQueryFilter || {}),
      ...(this._filterValueRecord || {}),
      nid: [...this.fixedIds]
    };

    const paginateResult: PaginateDto = { ...paginate };

    paginateResult.size = this.autoSize ? this.countFindElements : this.size;

    if (Array.isArray(this._pagesIds)) {
      const pageIds = this._pagesIds[this._currentPageNumber - 1];
      if (pageIds) {
        const currentIds = Array.isArray(filter['id']) ? filter['id'] : (filter['id'] ? [filter['id']] : []);
        filter['id'] = [
          ...currentIds,
          ...(pageIds.map(i => i._id))
        ];
      }
      paginateResult.page = 1;
    }

    return this._genericSrcSrv.listElements(url, {
      paginate: paginateResult, filter
    });
  }

  private _reloadFixedData(idList: Array<string>): Observable<AnyObject> {
    if (!this._gatewayUrl || !idList.length) return of(null);

    const url = this._gatewayUrl;

    this.isLoading = true;
    const filter = { id: idList, ...(this._extraQueryFilter || {}) };

    const paginate: PaginateDto = { size: idList.length, page: 1 };

    return this._genericSrcSrv.listElements(url, { paginate, filter })
      .pipe(
        tap(({ objects }) => this.fixedSources = objects),
        mergeMap(() => alwaysObservable(this._config.onLoadElements?.(this.getExtraData(null)))),
        tap(() => this.updateRender()),
        tap(() => this._socket?.emit('subscribe', (this.fixedSources || []).map(e => e._id))),
        catchError(() => {
          this._snackBar.open('Error al cargar los elementos fijos', 'Cerrar', {
            duration: 5000,
            panelClass: ['bg-warn-700']
          });
          return of([]);
        }),
        tap(() => {
          this.isLoading = false;
          this.scrollToElementId(idList[0]);
        })
      );
  }

  private _connectSocket(auth: AuthCache) {
    if (!this._gatewayUrl || !this._hasSocket || this._socket) return;

    const url = `${environment.apiUrl}/${this._gatewayUrl}`;

    const config: SocketIoConfig = { url, options: { query: { token: auth.session.token } } };

    this._socket = new Socket(config);
    this._socket.connect();

    this._socket.emit('subscribe', this.elements.map(e => e._id));

    this._subscriptions.add(
      this._socket.fromEvent('element')
        .pipe(
          tap((element: AnyObject) => {
            this.receiverSocketElement.emit(element);
            this._socketElementEvent$.next(element);
            if (this.renderedElementHashes[element._id]) {
              this._updateRenderElement(element);
              this.updateRender();
            }
          })
        ).subscribe()
    );

    this._subscriptions.add(
      this._socket.fromEvent('connect')
        .pipe(
          tap(() => this._socket?.emit('subscribe', this.elements.map(e => e._id)))
        ).subscribe()
    );
  }

  private _initDrawer(config: DynamicDrawer) {
    const { index, component, data, title } = config;
    const container = this.drawerContainers.get(index);

    if (!container) return;
    container.clear();

    this.dynamicDrawers[index].title = title;
    const ref = container.createComponent(component);

    Object.keys(data).forEach(key => ref.setInput(key, data[key]));

    this.drawerDynamics.get(index)?.open();
  }

  private _organizeColumns() {
    this.configKeys();
    const savedColumnKeys = storageManager.getJson<string[]>(this._columnConfigKey);

    if (savedColumnKeys) {
      this.showedColumns = savedColumnKeys
        .map(key => (this._columns || []).find(c => c.key === key))
        .filter((c): c is TableDataColumn => !!c);
    } else {
      this.showedColumns = [...(this._columns || [])];
    }

    this.columnKeys = ['$select', ...this.showedColumns.map(c => c.key)];

    const defaultSortColumn = (this._columns || []).find(c => !!c.sort);
    if (defaultSortColumn) {
      this._acsSort = true;
      this._currentSort = defaultSortColumn.sort;
    }
  }

  private _updateRenderElement(origin: AnyObject): RenderedElementHash {
    const _id = origin._id as string;
    const index = this.renderedElementHashes?.[_id]?.index ?? this.renderedElementList.length;

    const view = this.showedColumns.reduce((acc, column) => {
      acc[column.key] = tableGetCellRender(this.getExtraData(origin), column);
      return acc;
    }, {} as Record<string, AnyObject>);

    const middleView = this.showedColumns.reduce((acc, column, i) => {
      acc[column.key] = tableGetMiddleRender(this.getExtraDataMiddle(origin, i), column);
      return acc;
    }, {} as Record<string, AnyObject>);

    const middleViewClass = this.showedColumns.reduce((acc, column, i) => {
      acc[column.key] = tableGetMiddleClass(this.getExtraDataMiddle(origin, i), column);
      return acc;
    }, {} as Record<string, string>);

    let card: RenderedCardElement | undefined;
    if (this.structure) {
      card = {
        title: cardGetTitleRender(this.getExtraData(origin), this.structure),
        body: cardGetBodyRender(this.getExtraData(origin), this.structure) as AnyObject[],
        img: cardGetImgRender(this.getExtraData(origin), this.structure),
        icon: cardGetIconRender(this.getExtraData(origin), this.structure),
        actionIcons: this.actions.reduce((acc, action) => {
          acc[action.key] = cardGetActionIconRender(action, this.getExtraData(origin));
          return acc;
        }, {} as Record<string, string>)
      };
    }

    const rendered: RenderedElement = {
      _id,
      origin,
      table: { view, middleView, middleViewClass },
      card
    };

    if (index >= this.renderedElementList.length) {
      this.renderedElementList.push(rendered);
    } else {
      this.renderedElementList[index] = rendered;
    }

    const renderedHash = { rendered, index };
    this.renderedElementHashes[_id] = renderedHash;

    return renderedHash;
  }

  private _clearRenderedElements() {
    this.renderedElementList = [];
    this.renderedElementHashes = {};
  }

  private _updateRenderElements(elements: Array<AnyObject>, clear?: boolean) {
    if (clear) this._clearRenderedElements();

    elements
      .forEach(element => this._updateRenderElement(element));

    this.renderedElementList = [...this.renderedElementList];

  }

  private _reloadAllIfNoComplete(): Observable<AnyObject> {
    const elementCount = this.renderedElementList.length;
    const realCount = this.countFindElements;

    if (elementCount >= realCount) return of(null);

    return this._reloadData({ page: 1, size: realCount });
  }

  protected readonly Array = Array;

  toggleFix(element: AnyObject): void {
  }


  showDrawerFilter() {
    const filterConfig = this.filterFieldsDrawer;

    this.filterFieldsDrawer = [];
    this.drawerFilter.open();

    setTimeout(() => {
      this.filterFieldsDrawer = filterConfig;
    }, 50)
  }
}
